import type { CloudinaryAssetKey } from '@infinex/ui-assets';

export type CloudinaryPublicId = CloudinaryAssetKey | (string & {});

/**
 * Get the URL of a Cloudinary image.
 *
 * @param publicId - The public ID of the image.
 * @param transforms - Optional transformation options.
 * @returns The URL of the image.
 */
export function getCloudinaryImageUrl(
  publicId: CloudinaryPublicId,
  transforms?: CloudinaryImageTransforms
): string {
  const baseUrl = 'https://res.cloudinary.com/infinex/image/upload';
  return transforms
    ? `${baseUrl}/${transformations(transforms)}/${publicId}`
    : `${baseUrl}/${publicId}`;
}

// Helpers --------------------------------------------------------------------

function transformations(transforms: CloudinaryImageTransforms): string {
  const { crop, dpr, effect, format, gravity, height, quality, width } =
    transforms;
  const parts: string[] = [];

  if (width) parts.push(t('width', width));
  if (height) parts.push(t('height', height));
  if (crop) parts.push(t('crop', crop));
  if (gravity) parts.push(t('gravity', gravity));
  if (quality != null) parts.push(t('quality', quality));
  if (format) parts.push(t('format', format));
  if (dpr) parts.push(t('dpr', dpr));
  if (effect) {
    if (Array.isArray(effect)) {
      parts.push(effect.map((e) => t('effect', e)).join('/'));
    } else {
      parts.push(t('effect', effect));
    }
  }

  return parts.join(',');
}

const TRANSFORMERS = {
  crop: 'c',
  dpr: 'dpr',
  effect: 'e',
  format: 'f',
  gravity: 'g',
  height: 'h',
  quality: 'q',
  width: 'w',
};

/**
 * Build the transformation string for the given type and value.
 *
 * @param type - The type of transformation to apply to the image.
 * @param value - The value of the transformation to apply to the image.
 * @returns The transformation string for the given type and value.
 */
function t(type: keyof typeof TRANSFORMERS, value: number | string): string {
  return `${TRANSFORMERS[type]}_${value}`;
}

// Types -----------------------------------------------------------------------

export type CloudinaryImageTransforms = {
  /**
   * The width of the image. Must be a positive integer (px).
   */
  width?: number;
  /**
   * The height of the image. Must be a positive integer (px).
   */
  height?: number;
  /**
   * The type of crop/resize mode to apply when resizing the image.
   */
  crop?: Crop;
  /**
   * The gravity transformation to apply to the image.
   */
  gravity?: Gravity;
  /**
   * The quality of the returned image. Numeric values must be between 1 and 100.
   */
  quality?: Quality | number;
  /**
   * The device pixel ratio to apply to the image.
   */
  dpr?: 1 | 2;
  /**
   * The image format of the returned image. By default, the format of the
   * original uploaded image’s file extension.
   *
   * When `'auto'`, Cloudinary performs automatic format selection based on the
   * requesting browser.
   */
  format?: Format;
  /**
   * Optionally apply an effect to the image.
   * @see https://cloudinary.com/documentation/image_transformations#image_effects
   */
  effect?: Effect | Effect[];
};

type Crop = 'auto' | 'crop' | 'fill' | 'thumb';
type Quality = 'auto' | 'auto:best' | 'auto:good' | 'auto:eco' | 'auto:low';

type Format =
  | 'auto'
  | 'avif'
  | 'gif'
  | 'jpg'
  | 'jpeg'
  | 'jpeg-xl'
  | 'jpeg-xr'
  | 'png'
  | 'webp';

type Compass =
  | 'north'
  | 'north_east'
  | 'north_west'
  | 'west'
  | 'south_west'
  | 'south'
  | 'south_east'
  | 'east'
  | 'center';
type Gravity =
  | 'auto'
  | 'auto:subject'
  | 'face'
  | 'face:center'
  | 'faces'
  | 'faces:center'
  | Compass;

type Effect = ImageEffect | `${ImageEffect}:${number}`;
type ImageEffect =
  | 'blackwhite'
  | 'blur'
  | 'blur_faces'
  | 'brightness'
  | 'brightness_hsb'
  | 'contrast'
  | 'gamma'
  | 'grayscale'
  | 'improve'
  | 'pixelate'
  | 'pixelate_faces'
  | 'saturation'
  | 'sepia'
  | 'sharpen'
  | 'vibrance';
